import { type createTodoMutation, parseQuickAdd } from "@listto/shared";

/** Options accepted by createTodoMutation, derived so this module never drifts from it. */
export type QuickAddOpts = NonNullable<Parameters<typeof createTodoMutation>[3]>;

/** The subset of a list row that quick-add needs to resolve a `#tag` / `@list` token. */
export interface QuickAddList {
  id: string;
  name: string | null;
}

export interface QuickAddPreview {
  dateLabel: string | null;
  hasTime: boolean;
  dueDate: string | null;
  listToken: string | null;
  matchedListName: string | null;
}

/**
 * Finds the user's custom list whose name matches a `#tag` / `@list` token,
 * ignoring case and surrounding whitespace. Returns undefined when no list
 * matches, so the task falls back to the view's default list.
 */
export function findQuickAddList(
  lists: readonly QuickAddList[],
  name: string,
): QuickAddList | undefined {
  const target = name.trim().toLowerCase();
  return lists.find((l) => (l.name ?? "").trim().toLowerCase() === target);
}

/**
 * Turns raw quick-add input into a cleaned task title plus creation options
 * (due date and, when a list token matches, the list id).
 */
export function resolveQuickAdd(
  raw: string,
  lists: readonly QuickAddList[],
  now?: Date,
): { text: string; opts: QuickAddOpts } {
  const parsed = parseQuickAdd(raw, now);
  const opts: QuickAddOpts = {};
  if (parsed.dueDate) {
    opts.dueDate = parsed.dueDate;
  }
  if (parsed.listToken) {
    const list = findQuickAddList(lists, parsed.listToken);
    if (list) {
      opts.listId = list.id;
    }
  }
  return { text: parsed.title, opts };
}

/**
 * Live preview of what quick-add would detect. Returns null when the input is
 * blank or contains neither a date nor a list token, so the bar stays quiet.
 */
export function buildQuickAddPreview(
  raw: string,
  lists: readonly QuickAddList[],
  now?: Date,
): QuickAddPreview | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }
  const parsed = parseQuickAdd(trimmed, now);
  if (!parsed.dueDate && !parsed.listToken) {
    return null;
  }
  const match = parsed.listToken ? findQuickAddList(lists, parsed.listToken) : undefined;
  return {
    dateLabel: parsed.dateLabel,
    hasTime: parsed.hasTime,
    dueDate: parsed.dueDate,
    listToken: parsed.listToken,
    matchedListName: match?.name ?? null,
  };
}
